import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { Cluster } from '@cast/types';
import {
  useClusterScheduleAssignmentsMutation,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
} from '@cast/split-me/hooks/mutations/scheduled-rebalancing';
import {
  useCreateRebalancingScheduleMutation,
  useUpdateRebalancingScheduleMutation,
} from '@cast/split-me/hooks/mutations/kent';
import { getClusterRebalancingSchedulesQueryOptions } from '@cast/split-me/hooks/queries/kent';
import { getRebalancingSchedulesQueryOptions } from '@cast/split-me/hooks/queries/scheduled-rebalancing';
import { useIsKentRebalancingMode } from '@cast/split-me/features/cluster/rebalance';
import { addTimeZoneToCron } from '@cast/split-me/utils/cron';

import {
  OPTIMIZATION_GOAL_TO_KENT_SCHEDULE_PARAMS,
  OPTIMIZATION_GOAL_TO_SCHEDULE_PARAMS,
} from '../_constants';

import { OptimizationGoal } from '../open-api-types/autopilot-types';

// Kent rebalancing schedules are cluster-scoped so a fixed (DNS-1123-safe) name
// keeps create or update idempotent across runs.
const KENT_REBALANCING_SCHEDULE_NAME = 'ops-pilot-rebalance';

// Autoscaler schedules are org-scoped so a stable per-cluster name
// keeps create or update idempotent across runs.
const getAutoscalerScheduleName = (cluster: Cluster) =>
  `${cluster.name}-rebalance-${cluster.id.slice(0, 8)}`;

export const useScheduleRebalancingFlow = () => {
  const isKentRebalancingMode = useIsKentRebalancingMode();
  const queryClient = useQueryClient();

  const { mutateAsync: createSchedule } = useCreateScheduleMutation();
  const { mutateAsync: updateSchedule } = useUpdateScheduleMutation();
  const { mutateAsync: assignSchedule } =
    useClusterScheduleAssignmentsMutation();

  const { mutateAsync: createKentSchedule } =
    useCreateRebalancingScheduleMutation();
  const { mutateAsync: updateKentSchedule } =
    useUpdateRebalancingScheduleMutation();

  const scheduleRebalancing = useCallback(
    async ({
      cron,
      timeZone,
      optimizationGoal,
      cluster,
      organizationId,
    }: {
      cron: string;
      timeZone: string;
      optimizationGoal: OptimizationGoal;
      cluster: Cluster;
      organizationId: string;
    }): Promise<boolean> => {
      if (!optimizationGoal) {
        return false;
      }

      if (isKentRebalancingMode) {
        // Kent schedules are cluster-scoped and requires no separate job assignment.
        // creating or updating a schedule is the whole apply
        try {
          const kentSchedules = await queryClient.fetchQuery({
            ...getClusterRebalancingSchedulesQueryOptions({
              organizationId,
              clusterId: cluster.id,
            }),
            // The create-vs-update decision must see the latest state (retry
            // after a partial failure must find an already-created schedule),
            // so bypass the 20s global staleTime.
            staleTime: 0,
          });
          const existing = kentSchedules.find(
            (schedule) => schedule.name === KENT_REBALANCING_SCHEDULE_NAME
          );
          const presetParams =
            OPTIMIZATION_GOAL_TO_KENT_SCHEDULE_PARAMS[optimizationGoal];

          if (existing) {
            await updateKentSchedule({
              clusterId: cluster.id,
              id: existing.name!,
              updateRebalancingScheduleData: {
                ...presetParams,
                schedule: { cron },
              },
            });
          } else {
            await createKentSchedule({
              clusterId: cluster.id,
              createRebalancingScheduleData: {
                ...presetParams,
                name: KENT_REBALANCING_SCHEDULE_NAME,
                enabled: true,
                schedule: { cron },
              },
            });
          }
          return true;
        } catch {
          return false;
        }
      }

      try {
        const schedules = await queryClient.fetchQuery({
          ...getRebalancingSchedulesQueryOptions({ isInSecurity: false }),
          // The create-vs-update decision must see the latest state (retry
          // after a partial failure must find an already-created schedule),
          // so bypass the 20s global staleTime.
          staleTime: 0,
        });
        const scheduleName = getAutoscalerScheduleName(cluster);
        const existing = schedules?.find(
          (schedule) => schedule.name === scheduleName
        );
        const params = {
          ...OPTIMIZATION_GOAL_TO_SCHEDULE_PARAMS[optimizationGoal],
          name: scheduleName,
          schedule: { cron: addTimeZoneToCron(cron, timeZone) },
        };
        const { data: savedSchedule } = existing
          ? await updateSchedule({ ...params, id: existing.id })
          : await createSchedule(params);
        if (!savedSchedule?.id) {
          return false;
        }
        const assignmentResults = await assignSchedule({
          jobsToCreate: [
            {
              clusterId: cluster.id,
              scheduleId: savedSchedule.id,
              enabled: true,
            },
          ],
        });
        if (assignmentResults.some((result) => result.status === 'rejected')) {
          return false;
        }
        return true;
      } catch {
        return false;
      }
    },
    [
      isKentRebalancingMode,
      queryClient,
      createSchedule,
      updateSchedule,
      assignSchedule,
      createKentSchedule,
      updateKentSchedule,
    ]
  );

  return {
    scheduleRebalancing,
  };
};
